import React from "react";

import { App, Button, DatePicker, Form, Input, InputNumber } from "antd";
import { useTranslation } from "react-i18next";

import dayjs from "dayjs";

import { API_URL } from "../../../shared/utils/Util";
import { fetchT } from "../../../shared/utils/TypedUtil";
import { AdjustCustomerPointsRequest, AdjustCustomerPointsResponse } from "../../../types/api";

type AdjustCustomerPointsProp = {
  restaurantId: string;

  // If supplied, only allow adjusting points for this customer.
  email_or_phone?: string;

  onSuccess?: () => void;
};

export const AdjustCustomerPointsForm = (props: AdjustCustomerPointsProp) => {
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const [saveLoading, setSaveLoading] = React.useState(false);

  const [form] = Form.useForm();

  const handleSave = (req: AdjustCustomerPointsRequest) => {
    setSaveLoading(true);

    fetchT<AdjustCustomerPointsResponse>(
      "POST",
      `${API_URL}/v1/r/${props.restaurantId}/customer_points`,
      req,
      (result: AdjustCustomerPointsResponse) => {
        notification.success({
          message: t("success"),
          description: t("added_points_successfully", { delta: req.delta }),
        });
        form.resetFields();
        setSaveLoading(false);
        if (props.onSuccess) {
          props.onSuccess();
        }
      },
      (err: any) => {
        setSaveLoading(false);

        let description: string = "";
        try {
          description = JSON.parse(err)["message"];
        } catch (e) {
          description = t("error_occurred");
        }

        notification.error({
          message: t("error"),
          description: description,
        });
      }
    );
  };

  const initialValues: AdjustCustomerPointsRequest = {
    email_or_phone: props.email_or_phone ? props.email_or_phone : "",
    delta: 0,
    description: t("manually_added_by_restaurant"),
    expire_time: dayjs().startOf("day").add(6, "month"),
  };

  React.useEffect(() => {
    form.setFieldsValue({
      email_or_phone: props.email_or_phone ? props.email_or_phone : "",
    });
  }, [props.email_or_phone]);

  const formElem = (
    <Form
      form={form}
      labelCol={{ span: 10 }}
      labelAlign="left"
      wrapperCol={{ span: 16 }}
      layout="horizontal"
      colon={false}
      initialValues={initialValues}
      onFinish={handleSave}
    >
      <Form.Item
        label={t("email_or_phone")}
        name="email_or_phone"
        rules={[
          {
            required: true,
            message: t("email_or_phone_is_required"),
          },
        ]}
      >
        <Input disabled={!!props.email_or_phone} />
      </Form.Item>
      <Form.Item
        label={t("points")}
        name="delta"
        rules={[
          {
            required: true,
            message: t("points_is_required"),
          },
        ]}
      >
        <InputNumber max={1000} step={1} precision={0} />
      </Form.Item>
      <Form.Item label={t("description")} name="description">
        <Input />
      </Form.Item>
      <Form.Item
        label={t("expiration")}
        name="expire_time"
        rules={[
          {
            required: true,
            message: t("expiration_date_required_for_credits"),
          },
        ]}
      >
        <DatePicker />
      </Form.Item>
      <Form.Item>
        <Button type="primary" block htmlType="submit" loading={saveLoading} disabled={saveLoading}>
          {t("save")}
        </Button>
      </Form.Item>
    </Form>
  );

  return formElem;
};
